import type {
  PerformanceMonitorConfig,
  PerformanceReport,
  PerformanceSummary,
  Logger,
} from "./types";
import type { MetricsCollector } from "./metrics";
import type { WebVitalsTracker } from "./web-vitals";

export class PerformanceMonitor {
  private config: PerformanceMonitorConfig;
  private metricsCollector: MetricsCollector;
  private webVitalsTracker: WebVitalsTracker;
  private logger?: Logger;

  constructor(
    config: PerformanceMonitorConfig,
    metricsCollector: MetricsCollector,
    webVitalsTracker: WebVitalsTracker,
    logger?: Logger,
  ) {
    this.config = config;
    this.metricsCollector = metricsCollector;
    this.webVitalsTracker = webVitalsTracker;
    this.logger = logger;
  }

  async initialize(): Promise<void> {
    if (!this.config.enabled) {
      return;
    }

    await this.webVitalsTracker.initialize();

    // Setup performance observers
    this.setupPerformanceObservers();

    this.logger?.info("Performance monitor initialized");
  }

  private setupPerformanceObservers(): void {
    // Observe navigation timing
    if (typeof window !== "undefined" && "PerformanceObserver" in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          this.handlePerformanceEntry(entry);
        }
      });

      observer.observe({
        entryTypes: ["navigation", "resource", "paint", "layout-shift"],
      });
    }
  }

  private handlePerformanceEntry(entry: PerformanceEntry): void {
    switch (entry.entryType) {
      case "navigation":
        this.handleNavigationEntry(entry as PerformanceNavigationTiming);
        break;
      case "resource":
        this.handleResourceEntry(entry as PerformanceResourceTiming);
        break;
      case "paint":
        this.handlePaintEntry(entry);
        break;
      case "layout-shift":
        this.handleLayoutShiftEntry(entry as LayoutShift);
        break;
    }
  }

  handleNavigationEntry(entry: PerformanceNavigationTiming): void {
    const metrics = {
      "navigation.domContentLoaded":
        entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
      "navigation.load": entry.loadEventEnd - entry.loadEventStart,
      "navigation.firstPaint": entry.responseStart - entry.requestStart,
      "navigation.dns": entry.domainLookupEnd - entry.domainLookupStart,
      "navigation.tcp": entry.connectEnd - entry.connectStart,
      "navigation.ssl":
        entry.secureConnectionStart > 0
          ? entry.connectEnd - entry.secureConnectionStart
          : 0,
    };

    Object.entries(metrics).forEach(([name, value]) => {
      this.metricsCollector.recordTiming(name, value);
      this.checkThreshold(name, value);
    });
  }

  handleResourceEntry(entry: PerformanceResourceTiming): void {
    const duration = entry.responseEnd - entry.requestStart;
    const name = `resource.${entry.name.split("/").pop() || "unknown"}`;

    this.metricsCollector.recordTiming(name, duration, {
      type: entry.initiatorType,
      size: entry.transferSize,
      cached: entry.transferSize === 0 && entry.decodedBodySize > 0,
    });
  }

  handlePaintEntry(entry: PerformancePaintTiming): void {
    const name = `paint.${entry.name.replace("-", "")}`;
    this.metricsCollector.recordTiming(name, entry.startTime);
  }

  handleLayoutShiftEntry(entry: LayoutShift): void {
    if (!entry.hadRecentInput) {
      this.metricsCollector.recordMetric("layout-shift", entry.value, "custom");
    }
  }

  checkThreshold(name: string, value: number): void {
    const threshold = this.config.thresholds[name];
    if (threshold && value > threshold) {
      this.logger?.warn(`Performance threshold exceeded`, {
        name,
        value,
        threshold,
      });
      this.config.onThresholdExceeded?.(name, value, threshold);
    }
  }

  getPerformanceReport(): PerformanceReport {
    return {
      webVitals: this.webVitalsTracker.getMetrics(),
      customMetrics: this.metricsCollector.getMetrics(),
      summary: this.generateSummary(),
      recommendations: this.generateRecommendations(),
    };
  }

  private generateSummary(): PerformanceSummary {
    // Generate performance summary
    return {
      overallScore: this.calculateOverallScore(),
      criticalIssues: this.identifyCriticalIssues(),
      improvements: this.identifyImprovements(),
    };
  }

  private generateRecommendations(): string[] {
    const recommendations: string[] = [];

    // Analyze metrics and generate recommendations
    const metrics = this.metricsCollector.getMetrics();

    // Check for common performance issues
    const largeResources = metrics.filter(
      (m) => m.name.startsWith("resource.") && m.value > 5000,
    );

    if (largeResources.length > 0) {
      recommendations.push(
        "Consider optimizing large resources (>5s load time)",
      );
    }

    const slowNavigation = metrics.find(
      (m) => m.name === "navigation.load" && m.value > 3000,
    );

    if (slowNavigation) {
      recommendations.push(
        "Page load time exceeds 3 seconds, consider optimization",
      );
    }

    return recommendations;
  }

  private calculateOverallScore(): number {
    // Simple scoring algorithm based on various metrics
    const metrics = this.metricsCollector.getMetrics();
    const webVitals = this.webVitalsTracker.getMetrics();

    let score = 100;

    // Deduct points for poor Web Vitals
    webVitals.forEach((metric) => {
      if (metric.rating === "poor") score -= 20;
      else if (metric.rating === "needs-improvement") score -= 10;
    });

    // Deduct points for slow navigation
    const navigationLoad = metrics.find((m) => m.name === "navigation.load");
    if (navigationLoad) {
      if (navigationLoad.value > 5000) score -= 20;
      else if (navigationLoad.value > 3000) score -= 10;
    }

    return Math.max(0, score);
  }

  private identifyCriticalIssues(): string[] {
    const issues: string[] = [];
    const metrics = this.metricsCollector.getMetrics();
    const webVitals = this.webVitalsTracker.getMetrics();

    // Check for critical issues
    webVitals.forEach((metric) => {
      if (metric.rating === "poor") {
        issues.push(`Poor ${metric.name} rating: ${metric.value}`);
      }
    });

    const slowNavigation = metrics.find(
      (m) => m.name === "navigation.load" && m.value > 5000,
    );
    if (slowNavigation) {
      issues.push(`Very slow page load: ${slowNavigation.value}ms`);
    }

    return issues;
  }

  private identifyImprovements(): string[] {
    const improvements: string[] = [];
    const metrics = this.metricsCollector.getMetrics();

    // Check for improvement opportunities
    const resources = metrics.filter((m) => m.name.startsWith("resource."));
    const slowResources = resources.filter((m) => m.value > 2000);

    if (slowResources.length > 0) {
      improvements.push(`${slowResources.length} resources load slowly (>2s)`);
    }

    return improvements;
  }
}
